import { Box, Button, makeStyles, TextField, Typography } from '@material-ui/core'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import MUIDataTable from 'mui-datatables'
import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import ContentWrapper from '../Layout/ContentWrapper'
import VehicleManagementModal from './VehicleManagementModal'
import ReservationRequestForm from './ReservationRequestForm'
import ReservationDetailsModal from './ReservationDetailsModal'
import CancelReservationModal from './CancelReservationModal'
import ReservationManagementGrid from './ReservationManagementGrid'
import UpcomingReservationsGrid from './UpcomingReservationsGrid'
import ReservationTableLabels from './ReservationTableLabels'
import formatVehicleLabel from './formatVehicleLabel'
import useReservationClock, { canCancelReservation, getReservationStatus } from './reservationLifecycle'

const useStyles = makeStyles((theme) => ({
  filters: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    margin: theme.spacing(1, 0)
  },
  departureDate: {
    width: 200,
    '& .MuiOutlinedInput-notchedOutline': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.23)'
    }
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginLeft: 'auto'
  },
  rowActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  cancelButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main
  }
}))

const columns = [
  { name: 'ownerName', label: 'Rezervuesi', options: { sort: false, customBodyRender: (value) => value || '—' } },
  {
    name: 'departureDate',
    label: 'Data e nisjes',
    options: {
      sort: false,
      customBodyRender: (value) => (value ? moment(value).format('DD/MM/YYYY') : '')
    }
  },
  { name: 'departureTime', label: 'Ora e nisjes', options: { sort: false } },
  {
    name: 'vehicles',
    label: 'Automjetet',
    options: {
      sort: false,
      customBodyRender: (value) => (Array.isArray(value) ? value.map(formatVehicleLabel).join(', ') : '')
    }
  },
  { name: 'destination', label: 'Destinacioni', options: { sort: false } },
  {
    name: 'status',
    label: 'Statusi',
    options: {
      sort: false,
      customBodyRender: (value) =>
        ({ pending: 'Në pritje të miratimit', approved: 'Miratuar', rejected: 'Refuzuar', cancelled: 'Anuluar', completed: 'Përfunduar' })[
          value
        ] || value
    }
  }
]

const VehicleReservations = () => {
  const classes = useStyles()
  const location = useLocation()
  const [reservations, setReservations] = useState([])
  const [reservationsLoading, setReservationsLoading] = useState(true)
  const [reservationsError, setReservationsError] = useState('')
  const [reservationReload, setReservationReload] = useState(0)
  const [departureDate, setDepartureDate] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [requestOpen, setRequestOpen] = useState(false)
  const [detailsReservationId, setDetailsReservationId] = useState(null)
  const [cancellationReservation, setCancellationReservation] = useState(null)
  const [manageVehiclesOpen, setManageVehiclesOpen] = useState(false)
  const [vehicles, setVehicles] = useState([])
  const [vehiclesLoading, setVehiclesLoading] = useState(true)
  const [vehiclesError, setVehiclesError] = useState('')
  const [vehicleReload, setVehicleReload] = useState(0)
  const now = useReservationClock(reservations, () => setReservationReload((previous) => previous + 1))
  let canManageVehicles = false
  let currentUserId = null
  try {
    const currentUser = jwtDecode(localStorage.getItem('token'))
    currentUserId = currentUser.user_id
    canManageVehicles = [1, 5, 6].includes(currentUser.user_level)
  } catch {
    canManageVehicles = false
  }

  useEffect(() => {
    const reservationId = new URLSearchParams(location.search).get('reservationId')
    if (reservationId && /^[a-f\d]{24}$/i.test(reservationId)) setDetailsReservationId(reservationId)
  }, [location.search])

  useEffect(() => {
    let active = true
    setVehiclesLoading(true)
    setVehiclesError('')
    axios
      .get('vehicles')
      .then(({ data }) => {
        if (!data.success || !Array.isArray(data.data)) throw new Error('Vehicles unavailable')
        if (active) setVehicles(data.data)
      })
      .catch(() => {
        if (active) setVehiclesError('Automjetet nuk u ngarkuan. Provoni përsëri.')
      })
      .finally(() => {
        if (active) setVehiclesLoading(false)
      })
    return () => {
      active = false
    }
  }, [vehicleReload])

  useEffect(() => {
    let active = true
    setReservationsLoading(true)
    setReservationsError('')
    axios
      .get('vehicle-reservations')
      .then(({ data }) => {
        if (!data.success || !Array.isArray(data.data)) throw new Error('Reservations unavailable')
        if (active) setReservations(data.data)
      })
      .catch(() => {
        if (active) setReservationsError('Rezervimet nuk u ngarkuan. Provoni përsëri.')
      })
      .finally(() => {
        if (active) setReservationsLoading(false)
      })
    return () => {
      active = false
    }
  }, [reservationReload])

  const reservationSaved = (reservation) => {
    setReservations((previous) => [reservation, ...previous.filter((item) => item._id !== reservation._id)])
    setRequestOpen(false)
    setPage(0)
    setReservationReload((previous) => previous + 1)
  }

  const reservationCancelled = (reservation) => {
    setReservations((previous) => previous.map((item) => (item._id === reservation._id ? { ...item, ...reservation } : item)))
    setCancellationReservation(null)
    setReservationReload((previous) => previous + 1)
  }

  const vehicleSaved = (vehicle) => {
    setVehicles((previous) => [...previous.filter((item) => item._id !== vehicle._id), vehicle])
    setVehicleReload((previous) => previous + 1)
  }

  const vehicleDeleted = (vehicle) => {
    setVehicles((previous) => previous.filter((item) => item._id !== vehicle._id))
    setVehicleReload((previous) => previous + 1)
  }

  const visibleReservations = reservations.map((reservation) => ({ ...reservation, status: getReservationStatus(reservation, now) }))
  const filteredReservations = visibleReservations.filter((reservation) => {
    if (!departureDate || !moment(departureDate).isValid()) return true
    return moment(reservation.departureDate).isSame(departureDate, 'day')
  })

  const tableColumns = [
    ...columns,
    {
      name: '_id',
      label: 'Veprimet',
      options: {
        sort: false,
        customBodyRender: (id) => {
          const reservation = visibleReservations.find((item) => item._id === id)
          return (
            <Box className={classes.rowActions}>
              <Button variant='outlined' color='primary' size='small' onClick={() => setDetailsReservationId(id)}>
                Shiko detajet
              </Button>
              {reservation?.owner === currentUserId && canCancelReservation(reservation, now) && (
                <Button
                  variant='outlined'
                  size='small'
                  className={classes.cancelButton}
                  onClick={() => setCancellationReservation(reservation)}
                >
                  Anulo rezervimin
                </Button>
              )}
            </Box>
          )
        }
      }
    }
  ]

  const options = {
    download: false,
    print: false,
    filter: false,
    search: false,
    viewColumns: false,
    selectableRows: 'none',
    responsive: 'scroll',
    elevation: 4,
    customToolbar: () => (
      <DatePicker
        label='Data e nisjes'
        inputFormat='dd/MM/yyyy'
        value={departureDate}
        onChange={(value) => {
          setDepartureDate(value)
          setPage(0)
        }}
        componentsProps={{ actionBar: { actions: ['clear'] } }}
        renderInput={(params) => <TextField {...params} variant='outlined' size='small' className={classes.departureDate} />}
      />
    ),
    pagination: true,
    page,
    rowsPerPage,
    rowsPerPageOptions: [10, 50, 100, 200],
    onChangePage: setPage,
    onChangeRowsPerPage: (value) => {
      setRowsPerPage(value)
      setPage(0)
    },
    textLabels: new ReservationTableLabels(
      reservationsLoading ? 'Duke ngarkuar rezervimet…' : reservationsError ? 'Rezervimet nuk janë ngarkuar.' : 'Nuk ka rezervime'
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ContentWrapper>
        <div className='content-heading'>
          <div>Rezervimet e automjeteve</div>
        </div>
        <Box className={classes.filters}>
          <Box className={classes.actions}>
            {canManageVehicles && (
              <Button component={Link} to='/vehicle-reservations/reports' variant='outlined'>
                Raportet
              </Button>
            )}
            {canManageVehicles && (
              <Button
                variant='outlined'
                onClick={() => {
                  setManageVehiclesOpen(true)
                  setVehicleReload((previous) => previous + 1)
                }}
              >
                Menaxho automjetet
              </Button>
            )}
            <Button variant='contained' onClick={() => setRequestOpen(true)}>
              Kërko rezervim
            </Button>
          </Box>
        </Box>
        {reservationsError && (
          <Box>
            <Typography role='alert' color='error'>
              {reservationsError}
            </Typography>
            <Button onClick={() => setReservationReload((previous) => previous + 1)}>Provo përsëri</Button>
          </Box>
        )}
        <MUIDataTable title='Rezervimet tuaja te automjeteve' data={filteredReservations} columns={tableColumns} options={options} />
        <UpcomingReservationsGrid
          currentUserId={currentUserId}
          reloadKey={reservationReload}
          onChanged={() => setReservationReload((previous) => previous + 1)}
        />
        {canManageVehicles && (
          <ReservationManagementGrid reloadKey={reservationReload} onChanged={() => setReservationReload((previous) => previous + 1)} />
        )}
        {detailsReservationId && (
          <ReservationDetailsModal
            reservationId={detailsReservationId}
            onClose={() => setDetailsReservationId(null)}
            onChanged={() => setReservationReload((previous) => previous + 1)}
          />
        )}
        {cancellationReservation && (
          <CancelReservationModal
            reservation={cancellationReservation}
            onClose={() => setCancellationReservation(null)}
            onCancelled={reservationCancelled}
          />
        )}
        {requestOpen && (
          <ReservationRequestForm
            onClose={() => setRequestOpen(false)}
            onSaved={reservationSaved}
            onJoined={() => {
              setRequestOpen(false)
              setReservationReload((previous) => previous + 1)
            }}
          />
        )}
        {canManageVehicles && manageVehiclesOpen && (
          <VehicleManagementModal
            vehicles={vehicles}
            loading={vehiclesLoading}
            error={vehiclesError}
            onReload={() => setVehicleReload((previous) => previous + 1)}
            onClose={() => setManageVehiclesOpen(false)}
            onVehicleSaved={vehicleSaved}
            onVehicleDeleted={vehicleDeleted}
          />
        )}
      </ContentWrapper>
    </LocalizationProvider>
  )
}

export default VehicleReservations
